import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr, API } from "../lib/api";
import { industryByKey, ROLES, roleLabel } from "../lib/constants";
import { MODULES } from "../lib/constants";
import { Plus } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import EmailCard from "../components/settings/EmailCard";
import NotificationPrefs from "../components/settings/NotificationPrefs";
import ScheduledReports from "../components/settings/ScheduledReports";

function WorkflowTransitions({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState("");
  useEffect(() => { api.get("/workflow/config").then((r) => setRows(r.data.transitions)).catch(() => {}); }, []);
  const toggle = async (t) => {
    if (!isAdmin) return;
    setSaving(t.from_stage);
    try {
      const { data } = await api.patch("/workflow/config", { transitions: { [t.from_stage]: !t.auto } });
      setRows((rs) => rs.map((r) => ({ ...r, auto: data.transitions[r.from_stage] ?? r.auto })));
      toast.success(`${t.from_name} → ${t.to_name}: ${!t.auto ? "auto" : "manual"}`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(""); }
  };
  return (
    <div className="space-y-1" data-testid="workflow-transitions">
      {rows.map((t) => (
        <div key={t.from_stage} className="flex items-center gap-3 py-1.5 border-b border-[var(--c-border)]/50 last:border-0"
          data-testid={`wf-row-${t.from_stage}`}>
          <span className="text-[11px] text-[var(--c-text)] w-40">{t.from_name}</span>
          <span className="text-[var(--c-muted)]">→</span>
          <span className="text-[11px] text-[var(--c-muted)] w-40">{t.to_name}</span>
          <button data-testid={`wf-toggle-${t.from_stage}`} onClick={() => toggle(t)} disabled={!isAdmin || saving === t.from_stage}
            className={`ml-auto h-5 px-2 rounded-full text-[9px] font-mono uppercase tracking-wider border transition-colors disabled:opacity-50 ${
              t.auto ? "bg-[var(--c-green)]/15 border-[var(--c-green)]/50 text-[var(--c-green)]" : "bg-[var(--c-amber)]/15 border-[var(--c-amber)]/50 text-[var(--c-amber)]"}`}>
            {t.auto ? "✅ auto" : "⏸ manual"}
          </button>
        </div>
      ))}
      <div className="text-[10px] text-[var(--c-muted)] mt-2">Auto stages advance immediately when exit criteria are met. Manual stages show a confirmation banner on the project page.</div>
    </div>
  );
}

function DrawingTemplateCard({ isAdmin }) {
  const [meta, setMeta] = useState(null);
  const [styleNote, setStyleNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const styleRef = useRef(null);
  const load = () => api.get("/org/drawing-template").then(({ data }) => setMeta(data.template)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (ref, url, onDone) => {
    const f = ref.current?.files?.[0];
    if (!f) return toast.error("Choose a file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(url, fd);
      if (data.note) setStyleNote(data.note);
      toast.success("Uploaded");
      onDone && onDone(data);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const remove = async (url, msg) => {
    try { await api.delete(url); toast.success(msg); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="drawing-template-card">
      <h2 className="text-sm font-medium text-[var(--c-text)]">Drawing Template</h2>
      <p className="text-xs text-[var(--c-muted)] mt-1 leading-relaxed">
        Upload your company's DXF title block + border. Every generated drawing (2D sheets and the
        3D-derived manufacturing print) is merged onto this template. Text tokens like
        <span className="font-mono"> {"{{PROJECT}} {{DWG}} {{REV}} {{CLIENT}} {{DATE}} {{TITLE}} {{SCALE}}"} </span>
        are filled in automatically.
      </p>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <input ref={fileRef} type="file" accept=".dxf" data-testid="template-file"
          className="block text-xs text-[var(--c-muted)] file:bg-[var(--c-border)] file:border-0 file:rounded-sm file:px-3 file:py-1.5 file:text-xs file:text-[var(--c-text)] file:mr-3" />
        <Button size="sm" data-testid="template-upload-btn" disabled={!isAdmin || busy}
          onClick={() => upload(fileRef, "/org/drawing-template")}
          className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-7 text-xs">
          {busy ? "Uploading…" : "Upload Template"}
        </Button>
        {meta && (
          <Button size="sm" variant="outline" data-testid="template-remove-btn" disabled={!isAdmin}
            onClick={() => remove("/org/drawing-template", "Template removed")}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-red)] rounded-sm h-7 text-xs">
            Remove
          </Button>
        )}
      </div>
      {meta && (
        <div className="mt-3" data-testid="template-active">
          <div className="text-[11px] text-[var(--c-green)]">
            Active: <span className="font-mono">{meta.filename}</span> · uploaded {String(meta.uploaded_at || "").slice(0, 10)} by {meta.uploaded_by}
          </div>
          <div className="mt-2 border border-[var(--c-border)] rounded-sm bg-white p-1 max-w-lg">
            <img src={`${API}/org/drawing-template?svg=1`} alt="Template preview" className="w-full" data-testid="template-preview" />
          </div>
        </div>
      )}
      <div className="border-t border-[var(--c-border)] mt-4 pt-3">
        <h3 className="text-xs font-medium text-[var(--c-text)]">Style Reference (optional)</h3>
        <p className="text-[11px] text-[var(--c-muted)] mt-0.5">
          Upload a sample DXF/PDF drawing — a DXF reference donates its text height and dimension/callout colors to all generated sheets.
        </p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <input ref={styleRef} type="file" accept=".dxf,.pdf" data-testid="style-ref-file"
            className="block text-xs text-[var(--c-muted)] file:bg-[var(--c-border)] file:border-0 file:rounded-sm file:px-3 file:py-1.5 file:text-xs file:text-[var(--c-text)] file:mr-3" />
          <Button size="sm" variant="outline" data-testid="style-ref-upload-btn" disabled={!isAdmin || busy}
            onClick={() => upload(styleRef, "/org/style-reference")}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-7 text-xs">
            Upload Reference
          </Button>
          <Button size="sm" variant="outline" data-testid="style-ref-remove-btn" disabled={!isAdmin}
            onClick={() => remove("/org/style-reference", "Style reference removed")}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-red)] rounded-sm h-7 text-xs">
            Remove
          </Button>
        </div>
        {styleNote && <div className="text-[11px] text-[var(--c-green)] mt-2" data-testid="style-note">{styleNote}</div>}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const org = user?.org || {};
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [applyTarget, setApplyTarget] = useState({});
  const [showNew, setShowNew] = useState(false); // eslint-disable-line no-unused-vars -- reserved
  const [form, setForm] = useState({ name: "", email: "", role: "engineer", discipline: "" }); // eslint-disable-line no-unused-vars -- reserved
  const [disciplines, setDisciplines] = useState([]);
  const [newDiscipline, setNewDiscipline] = useState("");
  const [costs, setCosts] = useState([]);
  const [costFilter, setCostFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
      const [t, c, o, sc] = await Promise.all([api.get("/rule-templates"), api.get("/clients"), api.get("/org"), api.get("/standard-costs")]);
      setTemplates(t.data); setClients(c.data);
      setCosts(sc.data.costs || []);
      setDisciplines(o.data?.settings?.disciplines || []);
      if (isAdmin) {
        const a = await api.get("/audit");
        setAudit(a.data);
      }
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveDisciplines = async (list) => {
    try { await api.patch("/org/settings", { disciplines: list }); setDisciplines(list); toast.success("Disciplines updated"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const applyTemplate = async (t) => {
    const clientId = applyTarget[t.id];
    if (!clientId) { toast.error("Select a client first"); return; }
    try {
      const { data } = await api.post(`/rule-templates/${t.key}/apply`, { client_id: clientId });
      toast.success(`Template "${t.name}" applied — ${data.created} rules created`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Organization</div>
          <div className="text-base font-medium text-[var(--c-text)]" data-testid="settings-org-name">{org.name}</div>
          <div className="mt-4 space-y-2">
            {(org.industries || []).map((k) => (
              <div key={k} className="text-xs text-[var(--c-text)] flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: industryByKey(k).color }} />
                {industryByKey(k).name}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Modules</div>
          <div className="space-y-1.5">
            {MODULES.map((m) => {
              const on = (org.enabled_modules || []).includes(m.key);
              return (
                <div key={m.key} className="flex items-center justify-between text-xs" data-testid={`settings-module-${m.key}`}>
                  <span className={on ? "text-[var(--c-text)]" : "text-[var(--c-muted)]"}>{m.label}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-wider ${on ? "text-[var(--c-green)]" : "text-[var(--c-muted)]"}`}>{on ? "On" : "Off"}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Workflow Stages</div>
          <div className="space-y-1.5">
            {(org.workflow_stages || []).map((s, i) => (
              <div key={s.key} className="flex items-center justify-between text-xs" data-testid={`settings-stage-${s.key}`}>
                <span className="text-[var(--c-text)]"><span className="font-mono text-[var(--c-muted)] mr-2">{String(i + 1).padStart(2, "0")}</span>{s.name}</span>
                <span className="font-mono text-[10px] text-[var(--c-muted)]">{s.sla_days}d SLA</span>
              </div>
            ))}
          </div>
        </div>
        <DrawingTemplateCard isAdmin={isAdmin} />
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="disciplines-card">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Disciplines</div>
          <div className="space-y-1.5">
            {disciplines.map((d) => (
              <div key={d} className="flex items-center justify-between text-xs" data-testid={`discipline-${d.replace(/\s/g, "-")}`}>
                <span className="text-[var(--c-text)]">{d}</span>
                {isAdmin && (
                  <button data-testid={`discipline-remove-${d.replace(/\s/g, "-")}`}
                    onClick={() => saveDisciplines(disciplines.filter((x) => x !== d))}
                    className="font-mono text-[10px] text-[var(--c-muted)] hover:text-[var(--c-red)]">remove</button>
                )}
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="flex gap-2 mt-3">
              <Input data-testid="discipline-input" value={newDiscipline} onChange={(e) => setNewDiscipline(e.target.value)}
                placeholder="New discipline…" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs" />
              <Button size="sm" data-testid="discipline-add-btn"
                disabled={!newDiscipline.trim() || disciplines.includes(newDiscipline.trim())}
                onClick={() => { saveDisciplines([...disciplines, newDiscipline.trim()]); setNewDiscipline(""); }}
                className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-7 text-xs shrink-0">Add</Button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="workflow-transitions-card">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Workflow · Stage Transitions</div>
        <WorkflowTransitions isAdmin={isAdmin} />
      </div>

      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="notifications-card">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Notifications · per event × channel</div>
        <NotificationPrefs />
        <div className="border-t border-[var(--c-border)] mt-4 pt-4">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Scheduled Reports</div>
          <ScheduledReports isAdmin={isAdmin} users={users} />
        </div>
      </div>

      {isAdmin && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="email-settings-card">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Email · Resend</div>
          <EmailCard isAdmin={isAdmin} />
        </div>
      )}

      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="standard-costs-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Standard Costs · {costs.length} entries</div>
          <input data-testid="cost-search" value={costFilter} onChange={(e) => setCostFilter(e.target.value)}
            placeholder="Filter by description or key…"
            className="w-56 h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2 outline-none focus:border-[var(--c-accent)]" />
        </div>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full" data-testid="costs-table">
            <thead className="sticky top-0 bg-[var(--c-surface)]">
              <tr className="border-b border-[var(--c-border)]">
                {["Item Key", "Description", "Category", "Unit", "Unit Cost", "Currency", "Updated"].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {costs.filter((c) => !costFilter || c.description.toLowerCase().includes(costFilter.toLowerCase()) || c.item_key.includes(costFilter.toLowerCase()))
                .map((c) => (
                  <tr key={c.item_key} className="border-b border-[var(--c-border)]/60 last:border-0" data-testid={`cost-row-${c.item_key}`}>
                    <td className="px-2 py-1.5 text-[11px] font-mono text-[var(--c-accent)]">{c.item_key}</td>
                    <td className="px-2 py-1.5 text-[11px] text-[var(--c-text)]">{c.description}</td>
                    <td className="px-2 py-1.5 text-[10px] text-[var(--c-muted)]">{c.category}</td>
                    <td className="px-2 py-1.5 text-[10px] text-[var(--c-muted)]">{c.unit}</td>
                    <td className="px-2 py-1.5">
                      {isAdmin ? (
                        <input key={`${c.item_key}-${c.unit_cost}`} defaultValue={c.unit_cost}
                          data-testid={`cost-input-${c.item_key}`}
                          onBlur={async (e) => {
                            const v = parseFloat(e.target.value);
                            if (!v || v === c.unit_cost) return;
                            try { await api.post("/standard-costs", { ...c, unit_cost: v }); toast.success(`${c.item_key} → ₹${v}`); load(); }
                            catch (err) { toast.error(fmtErr(err)); }
                          }}
                          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                          className="w-24 h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] font-mono rounded-sm px-1.5 focus:border-[var(--c-accent)] outline-none" />
                      ) : (
                        <span className="text-[11px] font-mono text-[var(--c-text)]">{c.unit_cost}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-[10px] text-[var(--c-muted)]">{c.currency}</td>
                    <td className="px-2 py-1.5 text-[10px] font-mono text-[var(--c-muted)]">{c.last_updated}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-[var(--c-muted)] mt-2">Unit costs feed the BOM cost estimation. Accepted vendor quotes override these per project.</div>
      </div>

      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="rule-templates-section">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-4">Rule Templates · per industry</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((t) => (
            <div key={t.id} className="border border-[var(--c-border)] rounded-sm p-4" data-testid={`template-${t.key}`}>
              <div className="text-xs font-medium text-[var(--c-text)]">{t.name}</div>
              <div className="text-[10px] font-mono text-[var(--c-muted)] mt-0.5">{industryByKey(t.industry_id).name}</div>
              <div className="text-xs text-[var(--c-muted)] mt-1.5 leading-relaxed">{t.description}</div>
              <div className="font-mono text-[10px] text-[var(--c-accent)] mt-2">{t.rules.length} rules</div>
              {isAdmin && (
                <div className="flex gap-2 mt-3">
                  <Select value={applyTarget[t.id] || ""} onValueChange={(v) => setApplyTarget({ ...applyTarget, [t.id]: v })}>
                    <SelectTrigger data-testid={`template-client-${t.key}`} className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs flex-1"><SelectValue placeholder="Apply to client…" /></SelectTrigger>
                    <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                      {clients.map((c) => <SelectItem key={c.id} value={c.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" data-testid={`template-apply-${t.key}`} onClick={() => applyTemplate(t)}
                    className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-7 text-xs">Apply</Button>
                </div>
              )}
            </div>
          ))}
          {templates.length === 0 && <div className="text-xs text-[var(--c-muted)]">No rule templates seeded.</div>}
        </div>
      </div>

      {isAdmin && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="audit-section">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-4">Audit Trail</div>
          <div className="max-h-72 overflow-y-auto">
            {audit.map((a) => (
              <div key={a.id} className="flex items-center gap-4 py-2 border-b border-[var(--c-border)]/50 last:border-0 text-xs" data-testid={`audit-row-${a.action}`}>
                <span className="font-mono text-[10px] text-[var(--c-muted)] w-36 shrink-0">{String(a.created_at || "").slice(0, 19).replace("T", " ")}</span>
                <span className="text-[var(--c-text)] w-32 shrink-0 truncate">{a.user_name}</span>
                <span className={`font-mono text-[10px] uppercase tracking-wider w-16 shrink-0 ${
                  a.action === "create" ? "text-[var(--c-green)]" : a.action === "delete" ? "text-[var(--c-red)]" : "text-[var(--c-amber)]"}`}>{a.action}</span>
                <span className="text-[var(--c-muted)]">{a.entity} · {a.detail}</span>
              </div>
            ))}
            {audit.length === 0 && <div className="text-xs text-[var(--c-muted)]">No audit events yet.</div>}
          </div>
        </div>
      )}

    </div>
  );
}
